<div class="review-page p-3">
	<mat-card class="p-4">
		<div class="d-flex align-items-center flex-wrap header">
			<a mat-icon-button routerLink="/admin/settings" [queryParams]="{ section: 'recordings' }" matTooltip="{{ 'RECORDING_REVIEW.BACK' | translate }}"><mat-icon>arrow_back</mat-icon></a>
			<span class="title">{{ mediumName() }} · {{ formatDateTime(recording?.scheduledStart) }}</span>
			@if (recording) {
			<span class="status-chip ms-2" [ngClass]="'status-' + recording.status">{{ 'RECORDINGS.STATUSES.' + recording.status | translate }}</span>
			}
			@if (loading || saving) {
			<mat-spinner diameter="16" class="ms-2"></mat-spinner>
			}
		</div>

		@if (recording) {
		<div class="summary d-flex flex-wrap pb-2">
			<span>{{ 'RECORDING_REVIEW.ITEMS' | translate }}: <b>{{ items.length }}</b></span>
			@if (recording.newsStartSeconds !== null || recording.newsEndSeconds !== null) {
			<span>{{ 'RECORDING_REVIEW.NEWS' | translate }}: {{ mmss(recording.newsStartSeconds) }} – {{ mmss(recording.newsEndSeconds) }}</span>
			}
			@if (recording.broadcastStatus) {
			<span>{{ 'RECORDING_REVIEW.BROADCAST.' + recording.broadcastStatus | translate }}</span>
			}
			@if (recording.trimmedSeconds) {
			<span>{{ 'RECORDING_REVIEW.TRIMMED' | translate: { seconds: recording.trimmedSeconds } }}</span>
			}
			@if (recording.segmentationModel) {
			<span class="muted">{{ recording.segmentationModel }}</span>
			}
		</div>

		@if (recording.segmentationStatus === 'RUNNING' || recording.status === 'SEGMENTING') {
		<p class="info">
			<mat-spinner diameter="16"></mat-spinner>
			{{ 'RECORDING_REVIEW.RUNNING' | translate }}
		</p>
		}
		@if (recording.segmentationStatus === 'FAILED') {
		<p class="error">✖ {{ 'RECORDING_REVIEW.FAILED' | translate }}: {{ recording.segmentationError }}</p>
		}
		@if (recording.segmentationStatus === 'SKIPPED') {
		<p class="muted">{{ 'RECORDING_REVIEW.SKIPPED' | translate }} {{ recording.segmentationError }}</p>
		}

		@if (recording.segmentationNotes.length > 0) {
		<div class="notes">
			<div class="notes-title">⚠ {{ 'RECORDING_REVIEW.NOTES' | translate }} ({{ recording.segmentationNotes.length }})</div>
			<ul>
				@for (note of recording.segmentationNotes; track $index) {
				<li>{{ note }}</li>
				}
			</ul>
		</div>
		}

		@if (recording.transcriptionStatus === 'RUNNING') {
		<p class="info">
			<mat-spinner diameter="16"></mat-spinner>
			{{ 'RECORDING_REVIEW.TRANSCRIPTION_RUNNING' | translate }}
		</p>
		}
		@if (recording.transcriptionStatus === 'FAILED') {
		<p class="error">✖ {{ 'RECORDING_REVIEW.TRANSCRIPTION_FAILED' | translate }}: {{ recording.transcriptionError }}</p>
		}
		@if (recording.transcriptionNotes.length > 0) {
		<div class="notes">
			<div class="notes-title">⚠ {{ 'RECORDING_REVIEW.TRANSCRIPTION_NOTES' | translate }} ({{ recording.transcriptionNotes.length }})</div>
			<ul>
				@for (note of recording.transcriptionNotes; track $index) {
				<li>{{ note }}</li>
				}
			</ul>
		</div>
		}

		<div class="layout">
			<div class="player-col">
				@if (recording.videoUrl) {
				<video #player [src]="recording.videoUrl" controls preload="metadata" class="player" (timeupdate)="onTimeUpdate()"></video>
				} @else {
				<div class="no-video muted">{{ 'RECORDING_REVIEW.NO_VIDEO' | translate }}</div>
				}

				<div class="edition-review">
					<div class="d-flex align-items-center">
						<span>{{ 'RECORDING_REVIEW.MISSING' | translate }}</span>
						<button mat-icon-button (click)="changeMissing(-1)" [disabled]="saving || !recording.missingItems"><mat-icon>remove</mat-icon></button>
						<b class="missing-count">{{ recording.missingItems || 0 }}</b>
						<button mat-icon-button (click)="changeMissing(1)" [disabled]="saving"><mat-icon>add</mat-icon></button>
					</div>
					<div class="muted small">{{ 'RECORDING_REVIEW.REVIEWED_ITEMS' | translate: { done: reviewedCount(), total: items.length } }}</div>
					@if (recording.reviewedAt) {
					<div class="done-note">✔ {{ 'RECORDING_REVIEW.DONE_AT' | translate }} {{ formatDateTime(recording.reviewedAt) }}</div>
					}
				</div>
			</div>

			<div class="items-col">
				<!-- A3: these act on the whole edition, so they sit top-left, next to "Create clips". -->
				<div class="edition-actions-bar d-flex flex-wrap align-items-center">
					<button mat-flat-button color="primary" (click)="toggleDone()" [disabled]="saving || items.length === 0">
						<mat-icon>{{ recording.reviewedAt ? 'undo' : 'task_alt' }}</mat-icon>
						{{ (recording.reviewedAt ? 'RECORDING_REVIEW.UNMARK_DONE' : 'RECORDING_REVIEW.MARK_DONE') | translate }}
					</button>
					<button mat-stroked-button (click)="resegment()" [disabled]="saving || busy || !recording.audioUrl" matTooltip="{{ 'RECORDING_REVIEW.RESEGMENT_TOOLTIP' | translate }}">
						@if (saving || busy) {
						<mat-spinner diameter="15" class="me-1"></mat-spinner>
						} @else {
						<mat-icon>refresh</mat-icon>
						}
						{{ 'RECORDING_REVIEW.RESEGMENT' | translate }}
					</button>
					<button
						mat-stroked-button
						(click)="retranscribe()"
						[disabled]="retranscribing || transcribing || busy || !recording.videoUrl"
						matTooltip="{{ 'RECORDING_REVIEW.RETRANSCRIBE_TOOLTIP' | translate }}">
						@if (retranscribing || transcribing) {
						<mat-spinner diameter="15" class="me-1"></mat-spinner>
						} @else {
						<mat-icon>refresh</mat-icon>
						}
						{{ 'RECORDING_REVIEW.RETRANSCRIBE' | translate }}
					</button>
					@if (eligibleItems().length > 0) {
					<span class="spacer"></span>
					<mat-checkbox [checked]="allEligibleSelected()" [disabled]="creatingClips" (change)="toggleSelectAll()">
						{{ 'RECORDING_REVIEW.SELECT_ALL' | translate }}
					</mat-checkbox>
					<button mat-flat-button color="primary" (click)="createClips()" [disabled]="creatingClips || selectedItemIds.size === 0">
						@if (creatingClips) {
						<mat-spinner diameter="15" class="me-1"></mat-spinner>
						} @else {
						<mat-icon>movie_creation</mat-icon>
						}
						{{ 'RECORDING_REVIEW.CREATE_CLIPS' | translate: { count: selectedItemIds.size } }}
					</button>
					}
				</div>
				@if (!loading && items.length === 0 && !busy) {
				<p class="muted py-3">{{ 'RECORDING_REVIEW.NO_ITEMS' | translate }}</p>
				}
				@for (item of items; track item.id) {
				<div class="item" [class.active]="activeItemId === item.id" [ngClass]="item.reviewStatus ? 'verdict-' + item.reviewStatus : ''">
					<div class="item-head d-flex align-items-start">
						@if (item.clipId) {
						<mat-icon class="clip-done-icon" matTooltip="{{ 'RECORDING_REVIEW.CLIP_CREATED' | translate }}">check_circle</mat-icon>
						} @else if (item.reviewStatus !== 'EXTRA') {
						<mat-checkbox [checked]="selectedItemIds.has(item.id)" [disabled]="creatingClips" (change)="toggleItemSelection(item)"></mat-checkbox>
						}
						<span class="num">{{ item.itemNumber }}</span>
						<div class="item-main">
							<div class="item-title">
								{{ item.title }}
								@if (item.text) {
								<mat-icon class="has-text-icon" matTooltip="{{ 'RECORDING_REVIEW.HAS_TRANSCRIPTION' | translate }}">text_snippet</mat-icon>
								}
							</div>
							<div class="item-meta">
								{{ mmss(item.startSeconds) }} – {{ mmss(item.endSeconds) }} · {{ mmss(item.endSeconds - item.startSeconds) }}
								@if (item.author) {
								· {{ item.author }}
								}
								@if (item.confidence && item.confidence !== 'visoka') {
								<span class="confidence" [class.low]="item.confidence === 'niska'">{{ 'RECORDING_REVIEW.CONFIDENCE.' + item.confidence | translate }}</span>
								}
							</div>
							@if (item.originalStartSeconds !== null || item.originalEndSeconds !== null) {
							<div class="boundary-adjusted muted small">
								{{ 'RECORDING_REVIEW.ADJUSTED' | translate }}
								@if (item.originalStartSeconds !== null) {
								{{ mmss(item.originalStartSeconds) }} → {{ mmss(item.startSeconds) }}
								}
								@if (item.originalStartSeconds !== null && item.originalEndSeconds !== null) {
								·
								}
								@if (item.originalEndSeconds !== null) {
								{{ mmss(item.originalEndSeconds) }} → {{ mmss(item.endSeconds) }}
								}
							</div>
							}
							@if (item.warning) {
							<div class="item-warning">⚠ {{ item.warning }}</div>
							}
							@if (item.duplicateOfNewsItemId) {
							<div class="duplicate-badge muted small">
								🔁
								@if (item.anchorClipId) {
								{{ 'RECORDING_REVIEW.DUPLICATE.HAS_CLIP' | translate: { time: hhmm(item.anchorAiredAt) } }}
								} @else {
								{{ 'RECORDING_REVIEW.DUPLICATE.PENDING' | translate: { time: hhmm(item.anchorAiredAt), percent: item.matchPercentage } }}
								<a [routerLink]="['/admin/recordings', item.anchorRecordingId]">{{ 'RECORDING_REVIEW.DUPLICATE.OPEN_EDITION' | translate }}</a>
								}
								<button mat-button class="not-duplicate" (click)="markNotDuplicate(item)">{{ 'RECORDING_REVIEW.DUPLICATE.NOT_DUPLICATE' | translate }}</button>
							</div>
							}
							@if (expandedTextItemId === item.id && item.text) {
							<div class="item-text">{{ item.text }}</div>
							}
							@if (item.clipId) {
							<div class="clip-created-note">✓ {{ 'RECORDING_REVIEW.CLIP_CREATED' | translate }}</div>
							}
							@if (authorFeedback(item)) {
							<div class="muted small author-feedback">{{ authorFeedback(item) }}</div>
							}
							@if (expandedClipItemId === item.id && item.clipId) {
							<div class="item-text clip-preview">
								<div>{{ 'RECORDING_REVIEW.CLIP_ID' | translate }}: {{ item.clipId }}</div>
								<div>{{ 'RECORDING_REVIEW.CLIP_MEDIUM' | translate }}: {{ mediumName() }}</div>
								<div>{{ item.title }}</div>
							</div>
							}
						</div>
					</div>
					<div class="item-actions d-flex flex-wrap align-items-center">
						<button mat-stroked-button class="play" (click)="playStart(item)" [disabled]="!recording.videoUrl" matTooltip="„{{ item.firstWords }}…“">
							<mat-icon>play_arrow</mat-icon>{{ 'RECORDING_REVIEW.PLAY_START' | translate }}
						</button>
						<button mat-stroked-button class="play" (click)="playEnd(item)" [disabled]="!recording.videoUrl" matTooltip="„…{{ item.lastWords }}“">
							<mat-icon>play_arrow</mat-icon>{{ 'RECORDING_REVIEW.PLAY_END' | translate }}
						</button>
						<button mat-stroked-button class="play" (click)="toggleText(item)" [disabled]="!item.text" matTooltip="{{ item.text ? '' : ('RECORDING_REVIEW.NO_TEXT_YET' | translate) }}">
							<mat-icon>{{ expandedTextItemId === item.id ? 'expand_less' : 'description' }}</mat-icon>{{ 'RECORDING_REVIEW.TEXT' | translate }}
						</button>
						@if (item.clipId) {
						<button mat-stroked-button class="play" (click)="toggleClipPreview(item)">
							<mat-icon>{{ expandedClipItemId === item.id ? 'expand_less' : 'visibility' }}</mat-icon>{{ 'RECORDING_REVIEW.CLIP_PREVIEW' | translate }}
						</button>
						}
						<button mat-stroked-button class="play" (click)="toggleBoundaryEdit(item)" [disabled]="!recording.videoUrl">
							<mat-icon>{{ editingBoundariesItemId === item.id ? 'expand_less' : 'straighten' }}</mat-icon>{{ 'RECORDING_REVIEW.BOUNDARIES' | translate }}
						</button>
						@if (!item.clipId) {
						<button mat-stroked-button class="play" (click)="toggleSplit(item)">
							<mat-icon>{{ splittingItemId === item.id ? 'expand_less' : 'call_split' }}</mat-icon>{{ 'RECORDING_REVIEW.SPLIT' | translate }}
						</button>
						}
						<span class="spacer"></span>
						@for (verdict of verdicts; track verdict) {
						<button class="verdict" [class.selected]="item.reviewStatus === verdict" [ngClass]="'v-' + verdict" (click)="setVerdict(item, verdict)">
							{{ 'RECORDING_REVIEW.VERDICTS.' + verdict | translate }}
						</button>
						}
					</div>
					@if (editingBoundariesItemId === item.id) {
					<div class="boundary-editor d-flex flex-wrap align-items-end">
						<div class="field">
							<label>{{ 'RECORDING_REVIEW.BOUNDARY_START' | translate }}</label>
							<div class="d-flex align-items-center">
								<input type="text" [(ngModel)]="editStartText" placeholder="мм:сс" />
								<button mat-stroked-button class="from-player" (click)="setStartFromPlayer()" [disabled]="!recording.videoUrl">
									{{ 'RECORDING_REVIEW.SET_FROM_PLAYER' | translate }}
								</button>
							</div>
						</div>
						<div class="field">
							<label>{{ 'RECORDING_REVIEW.BOUNDARY_END' | translate }}</label>
							<div class="d-flex align-items-center">
								<input type="text" [(ngModel)]="editEndText" placeholder="мм:сс" />
								<button mat-stroked-button class="from-player" (click)="setEndFromPlayer()" [disabled]="!recording.videoUrl">
									{{ 'RECORDING_REVIEW.SET_FROM_PLAYER' | translate }}
								</button>
							</div>
						</div>
						<button mat-flat-button color="primary" (click)="saveBoundaries(item)" [disabled]="savingBoundaries">
							@if (savingBoundaries) {
							<mat-spinner diameter="15"></mat-spinner>
							} @else {
							{{ 'BUTTON.SAVE' | translate }}
							}
						</button>
						<button mat-stroked-button (click)="toggleBoundaryEdit(item)" [disabled]="savingBoundaries">
							{{ 'BUTTON.CANCEL' | translate }}
						</button>
						@if (boundariesError) {
						<div class="boundary-error">{{ boundariesError }}</div>
						}
					</div>
					}
					@if (splittingItemId === item.id) {
					<div class="split-editor">
						<div class="split-hint muted small">{{ 'RECORDING_REVIEW.SPLIT_HINT' | translate }}</div>
						@for (point of splitPointTexts; track $index) {
						<div class="split-row d-flex flex-wrap align-items-end">
							<div class="field">
								<label>{{ 'RECORDING_REVIEW.SPLIT_POINT' | translate: { n: $index + 2 } }}</label>
								<input type="text" [(ngModel)]="splitPointTexts[$index]" placeholder="мм:сс" />
							</div>
							<div class="field title-field">
								<label>{{ 'RECORDING_REVIEW.SPLIT_TITLE' | translate: { n: $index + 2 } }}</label>
								<input type="text" [(ngModel)]="splitTitleTexts[$index]" />
							</div>
							@if (splitPointTexts.length > 1) {
							<button mat-icon-button class="remove-point" (click)="removeSplitPoint($index)" [disabled]="splitting">
								<mat-icon>close</mat-icon>
							</button>
							}
						</div>
						}
						<button mat-stroked-button type="button" class="add-point" (click)="addSplitPoint(item)" [disabled]="splitPointTexts.length >= maxSplitPoints || splitting">
							<mat-icon>add</mat-icon>{{ 'RECORDING_REVIEW.SPLIT_ADD_POINT' | translate }}
						</button>
						<div class="split-actions d-flex flex-wrap align-items-center">
							<button mat-flat-button color="primary" (click)="submitSplit(item)" [disabled]="splitting">
								@if (splitting) {
								<mat-spinner diameter="15"></mat-spinner>
								} @else {
								{{ 'RECORDING_REVIEW.SPLIT' | translate }}
								}
							</button>
							<button mat-stroked-button (click)="toggleSplit(item)" [disabled]="splitting">
								{{ 'BUTTON.CANCEL' | translate }}
							</button>
						</div>
						@if (splitError) {
						<div class="boundary-error">{{ splitError }}</div>
						}
					</div>
					}
				</div>
				}
			</div>
		</div>
		}
	</mat-card>
</div>
